import { useAuthContext } from '@/auth/useAuthContext';
import { Modal } from '@/components/Modal';
import {
  ApiError,
  createMembershipTier,
  listMembershipTiers,
  updateMembershipTier,
} from '@/lib/api';
import type {
  CreateMembershipTierInput,
  MembershipTier,
  UpdateMembershipTierInput,
} from '@/lib/types';
import { Pencil, Plus, Repeat, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£',
  USD: '$',
  EUR: '€',
  CAD: '$',
  AUD: '$',
  NZD: '$',
};

function currencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency] ?? currency;
}

function majorToMinor(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return Math.round(parsed * 100);
}

function minorToMajor(minor: number): string {
  return (minor / 100).toFixed(2);
}

// ---------------------------------------------------------------------------
// Editor drawer
// ---------------------------------------------------------------------------

interface EditorFormState {
  name: string;
  description: string;
  visitsPerCycleText: string;
  priceText: string;
  rolloverUnused: boolean;
  active: boolean;
}

function emptyForm(): EditorFormState {
  return {
    name: '',
    description: '',
    visitsPerCycleText: '',
    priceText: '',
    rolloverUnused: false,
    active: true,
  };
}

function formFromTier(tier: MembershipTier): EditorFormState {
  return {
    name: tier.name,
    description: tier.description ?? '',
    visitsPerCycleText: String(tier.visitsPerCycle),
    priceText: minorToMajor(tier.priceMinor),
    rolloverUnused: tier.rolloverUnused === 1,
    active: tier.active === 1,
  };
}

interface EditorDrawerProps {
  existing: MembershipTier | null; // null = create
  currency: string;
  onClose: () => void;
  onSaved: () => void;
}

function EditorDrawer({ existing, currency, onClose, onSaved }: EditorDrawerProps) {
  const [form, setForm] = useState<EditorFormState>(() =>
    existing ? formFromTier(existing) : emptyForm(),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverIssues, setServerIssues] = useState<string[] | null>(null);
  const [showPriceConfirm, setShowPriceConfirm] = useState(false);

  // Detect if the user has changed price or currency vs the existing tier.
  function isPriceChange(): boolean {
    if (!existing) return false;
    const newMinor = majorToMinor(form.priceText);
    return newMinor !== null && newMinor !== existing.priceMinor;
  }

  async function doSave() {
    setBusy(true);
    setError(null);
    setServerIssues(null);

    const nameTrimmed = form.name.trim();
    if (nameTrimmed.length === 0) {
      setError('Name is required');
      setBusy(false);
      return;
    }

    const visitsPerCycle = Number(form.visitsPerCycleText);
    if (!Number.isInteger(visitsPerCycle) || visitsPerCycle <= 0) {
      setError('Visits per cycle must be a positive integer');
      setBusy(false);
      return;
    }

    const priceMinor = majorToMinor(form.priceText);
    if (priceMinor === null || priceMinor <= 0) {
      setError('Price must be a positive amount');
      setBusy(false);
      return;
    }

    try {
      if (existing) {
        const patch: UpdateMembershipTierInput = {
          name: nameTrimmed,
          description: form.description.trim() || null,
          visitsPerCycle,
          priceMinor,
          rolloverUnused: form.rolloverUnused,
          active: form.active,
        };
        await updateMembershipTier(existing.id, patch);
      } else {
        const input: CreateMembershipTierInput = {
          name: nameTrimmed,
          description: form.description.trim() || null,
          visitsPerCycle,
          priceMinor,
          rolloverUnused: form.rolloverUnused,
          active: form.active,
        };
        await createMembershipTier(input);
      }
      onSaved();
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        const issues = (err.body as { issues?: unknown } | null)?.issues;
        if (Array.isArray(issues) && issues.every((s) => typeof s === 'string')) {
          setServerIssues(issues as string[]);
        } else {
          setError(err.code);
        }
      } else {
        setError('Something went wrong');
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleSave() {
    // If updating price, require confirmation first.
    if (isPriceChange()) {
      setShowPriceConfirm(true);
      return;
    }
    await doSave();
  }

  async function handleConfirmedSave() {
    setShowPriceConfirm(false);
    await doSave();
  }

  return (
    <Modal
      onClose={onClose}
      maxWidth="max-w-md"
      aria-label={existing ? 'Edit membership tier' : 'New membership tier'}
    >
      <div className="flex flex-col p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-medium">
            {existing ? 'Edit membership tier' : 'New membership tier'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:text-foreground press"
            aria-label="Close"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>

        {showPriceConfirm ? (
          <div className="flex flex-1 flex-col justify-between">
            <div className="space-y-3">
              <p className="text-sm font-medium">Changing the price creates a new Stripe Price.</p>
              <p className="text-sm text-muted-foreground">
                Existing subscribers stay on the old price; new subscribers use the new one.
              </p>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowPriceConfirm(false)}
                className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void handleConfirmedSave()}
                className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
              >
                {busy ? 'Saving…' : 'OK, update price'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label
                  htmlFor="mt-name"
                  className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Name
                </label>
                <input
                  id="mt-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Gold Monthly Plan"
                  maxLength={120}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>

              {/* Description */}
              <div>
                <label
                  htmlFor="mt-description"
                  className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                >
                  Description (optional)
                </label>
                <textarea
                  id="mt-description"
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Unlimited daycare visits — save 20% vs pay-as-you-go"
                  rows={2}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>

              {/* Visits per cycle + price */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="mt-visits"
                    className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    Visits / month
                  </label>
                  <input
                    id="mt-visits"
                    type="number"
                    min={1}
                    step={1}
                    value={form.visitsPerCycleText}
                    onChange={(e) => setForm((p) => ({ ...p, visitsPerCycleText: e.target.value }))}
                    placeholder="20"
                    className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
                <div>
                  <label
                    htmlFor="mt-price"
                    className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    Price / month ({currencySymbol(currency)})
                  </label>
                  <input
                    id="mt-price"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={form.priceText}
                    onChange={(e) => setForm((p) => ({ ...p, priceText: e.target.value }))}
                    placeholder="49.99"
                    className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>

              {/* Flags */}
              <div className="space-y-2">
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.rolloverUnused}
                    onChange={(e) => setForm((p) => ({ ...p, rolloverUnused: e.target.checked }))}
                    className="rounded border-border"
                  />
                  Roll over unused visits to next cycle
                </label>
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
                    className="rounded border-border"
                  />
                  Active (visible for new subscriptions)
                </label>
              </div>

              {/* Errors */}
              {error !== null && <p className="text-sm text-destructive">{error}</p>}
              {serverIssues !== null && (
                <ul className="space-y-1 text-sm text-destructive">
                  {serverIssues.map((iss) => (
                    <li key={iss}>• {iss}</li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void handleSave()}
                className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
              >
                {busy ? 'Saving…' : 'Save tier'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type ModalState = { kind: 'none' } | { kind: 'add' } | { kind: 'edit'; tier: MembershipTier };

export function MembershipsSettings() {
  const { state } = useAuthContext();
  const account = state.me?.activeAccount;
  const currency = account?.currency ?? 'GBP';

  const [tiers, setTiers] = useState<MembershipTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [modal, setModal] = useState<ModalState>({ kind: 'none' });

  const refresh = useCallback(async () => {
    setLoading(true);
    setPermissionDenied(false);
    try {
      const res = await listMembershipTiers();
      setTiers(res.tiers);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setPermissionDenied(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (permissionDenied) {
    return (
      <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
        <p className="text-sm font-medium text-foreground">
          You don't have permission to manage membership tiers
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Ask an owner or manager to grant access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-medium tracking-tight">Memberships</h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            Daycare memberships — customers subscribe to a recurring monthly plan that includes a
            fixed number of daycare visits. Charged on the tenant's Stripe Connect account; no
            platform fee.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModal({ kind: 'add' })}
          className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press"
        >
          <Plus className="size-4" strokeWidth={1.75} />
          New tier
        </button>
      </header>

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-14 animate-pulse rounded-lg bg-secondary"
              style={{ opacity: 1 - i * 0.2 }}
            />
          ))}
        </div>
      ) : tiers.length === 0 ? (
        <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
          <Repeat className="mx-auto mb-3 size-6 text-muted-foreground" strokeWidth={1.5} />
          <p className="text-sm font-medium text-foreground">No membership tiers configured.</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Add a tier to offer customers a monthly subscription with included daycare visits.
          </p>
          <button
            type="button"
            onClick={() => setModal({ kind: 'add' })}
            className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press"
          >
            <Plus className="size-4" strokeWidth={1.75} />
            New tier
          </button>
        </div>
      ) : (
        <section className="overflow-hidden rounded-2xl bg-surface ring-1 ring-border elevate">
          <div className="grid grid-cols-[1.5fr_7rem_7rem_7rem_auto] gap-3 border-b border-border px-6 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            <span>Name</span>
            <span>Visits / mo</span>
            <span>Price / mo</span>
            <span>Status</span>
            <span />
          </div>
          <div className="divide-y divide-border">
            {tiers.map((tier) => (
              <div
                key={tier.id}
                className="grid grid-cols-[1.5fr_7rem_7rem_7rem_auto] items-center gap-3 px-6 py-3.5 text-sm"
              >
                <div>
                  <p className="font-medium">{tier.name}</p>
                  {tier.description && (
                    <p className="mt-0.5 text-xs text-muted-foreground">{tier.description}</p>
                  )}
                </div>
                <span className="num text-foreground">{tier.visitsPerCycle}</span>
                <span className="num text-foreground">
                  {currencySymbol(tier.currency)}
                  {minorToMajor(tier.priceMinor)}
                </span>
                <span>
                  {tier.active === 1 ? (
                    <span className="inline-flex items-center rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-medium leading-none text-success ring-hairline">
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium leading-none text-muted-foreground ring-hairline">
                      Archived
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => setModal({ kind: 'edit', tier })}
                  className="rounded-md p-1 text-muted-foreground hover:text-foreground press"
                  title="Edit tier"
                >
                  <Pencil className="size-3.5" strokeWidth={1.75} />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {modal.kind === 'add' && (
        <EditorDrawer
          existing={null}
          currency={currency}
          onClose={() => setModal({ kind: 'none' })}
          onSaved={() => {
            setModal({ kind: 'none' });
            void refresh();
          }}
        />
      )}

      {modal.kind === 'edit' && (
        <EditorDrawer
          existing={modal.tier}
          currency={currency}
          onClose={() => setModal({ kind: 'none' })}
          onSaved={() => {
            setModal({ kind: 'none' });
            void refresh();
          }}
        />
      )}
    </div>
  );
}
